import { createFileRoute } from "@tanstack/react-router";
import { BASE_URLS } from "common/constants/env";
import { validatePresent } from "common/utils/formValidators";
import AdminPage from "lib/components/admin/builders/AdminPage";
import SectionHeader from "lib/components/builders/communities/plans/SectionHeader";
import useMessage from "lib/hooks/useMessage";
import { useEffect, useState } from "react";
import Button from "ui/buttons/Button";
import Loading from "ui/components/Loading";
import Checkbox from "ui/forms/CheckboxOld";
import InputGroupText from "ui/forms/InputGroupText";
import Textarea from "ui/forms/Textarea";
import withAuthentication from "../../components/withAuthentication";
import styles from "./iframe-test.module.scss";

function IframeTestPage() {
  const [allowClipboard, setAllowClipboard] = useState(false);
  const [showMessageControls, setShowMessageControls] = useState(false);
  const [clipboardPermissions, setClipboardPermissions] = useState("");
  const [url, setUrl] = useState("");
  const [width, setWidth] = useState("100%");
  const [height, setHeight] = useState("100%");
  const [messageType, setMessageType] = useState("");
  const [messagePayload, setMessagePayload] = useState("{}");
  const [messageHistoryText, setMessageHistoryText] = useState("");

  const { messageHistory } = useMessage("SHOWROOM:OPTION_CHOICES_UPDATED");

  useEffect(() => {
    setMessageHistoryText(JSON.stringify(messageHistory, null, 2));
  }, [messageHistory]);

  useEffect(() => {
    if (allowClipboard) {
      setClipboardPermissions(
        `clipboard-write self ${BASE_URLS.local} ${BASE_URLS.develop} ${BASE_URLS.stage} ${BASE_URLS.prod}`,
      );
    } else {
      setClipboardPermissions("");
    }
  }, [allowClipboard]);

  return (
    <AdminPage currentPage="debug" pageTitle="Debug - iframe Test">
      <div className={styles.wrapper}>
        <SectionHeader title="Embedded Higharc Test Page" subtitle="Edit 'iframe' attributes below" hr />
        <form className="iframe-form">
          <div>
            <InputGroupText
              type="text"
              labelText="Page URL"
              inputValue={url}
              onChange={(url) => setUrl(url)}
              validate={() => validatePresent("URL", url)}
            />
            <InputGroupText
              type="text"
              labelText="Width"
              inputValue={width}
              onChange={(width) => setWidth(width)}
              validate={() => validatePresent("Width", width)}
            />
            <InputGroupText
              type="text"
              labelText="Height"
              inputValue={height}
              onChange={(height) => setHeight(height)}
              validate={() => validatePresent("Height", height)}
            />
            <Checkbox
              className="iframe-form-checkbox"
              label="Allow clipboard access"
              checked={allowClipboard}
              onChange={() => setAllowClipboard(!allowClipboard)}
            />
            <Checkbox
              className="iframe-form-checkbox"
              label="Test iframe communication"
              checked={showMessageControls}
              onChange={() => setShowMessageControls(!showMessageControls)}
            />
            <Button
              className="iframe-button"
              onClick={() => {
                const iframe: HTMLIFrameElement = document.getElementById("higharc-iframe") as HTMLIFrameElement;
                iframe.src = "";

                setTimeout(() => {
                  iframe.src = url;

                  setMessageHistoryText("");
                  setMessagePayload("{}");
                }, 100);
              }}
              btnStyle="primary"
              btnSize="large"
              type="button"
            >
              Reload
            </Button>
          </div>
          {showMessageControls && (
            <>
              <div>
                <InputGroupText
                  type="text"
                  labelText="Message Type"
                  inputValue={messageType}
                  onChange={(messageType) => setMessageType(messageType)}
                />
                <Button
                  id="iframe-message-button"
                  className="iframe-button"
                  btnStyle="primary"
                  btnSize="large"
                  type="button"
                  onClick={() => {
                    const iframe: HTMLIFrameElement = document.getElementById("higharc-iframe") as HTMLIFrameElement;
                    let parsedData = {};

                    try {
                      parsedData = JSON.parse(messagePayload);
                    } catch (e) {
                      parsedData = { message: messagePayload };
                    }

                    iframe?.contentWindow?.postMessage(
                      {
                        messageType,
                        data: parsedData,
                      },
                      "*",
                    );
                  }}
                >
                  Send Message
                </Button>
              </div>
              <div className="iframe-message">
                <Textarea
                  name="messagePayload"
                  value={messagePayload}
                  onChange={(e) => setMessagePayload(e.target.value)}
                />
                <Textarea name="messageHistory" value={messageHistoryText} readOnly />
              </div>
            </>
          )}
        </form>
        <div className="iframe-container">
          {url ? (
            <iframe id="higharc-iframe" src={url} allow={clipboardPermissions} height={height} width={width} />
          ) : (
            <Loading variant="fill" text="Set 'iframe' URL to continue..." />
          )}
        </div>
      </div>
    </AdminPage>
  );
}

export const Route = createFileRoute("/debug/iframe-test")({
  component: withAuthentication(IframeTestPage, { superAdmin: true }),
});
